import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

/**
 * Tells first-run screens (e.g. the dashboard onboarding tour) when the AI usage disclaimer no longer stands
 * in their way, so the two never open stacked on top of each other.
 *
 * Starts closed on every page load. `AppComponent` opens it once the disclaimer check finds the disclaimer
 * already confirmed, or once the user closes (confirms) the disclaimer dialog. A disclaimer that is never
 * confirmed keeps it closed for the whole session, so the next session shows the disclaimer first again.
 */
@Injectable({
  providedIn: 'root',
})
export class AiDisclaimerGateService {
  private readonly released = new BehaviorSubject<boolean>(false);

  /** Emits once, as soon as the disclaimer is out of the way (immediately if it already is), then completes. */
  readonly released$: Observable<void> = this.released.pipe(
    filter(isReleased => isReleased),
    take(1),
    map(() => undefined),
  );

  get isReleased(): boolean {
    return this.released.value;
  }

  release(): void {
    if (!this.released.value) {
      this.released.next(true);
    }
  }
}
